import type * as PIXI from 'pixi.js';
import type { Player } from '../entities/Player';
import type { Enemy } from '../entities/Enemy';
import { Projectile } from '../entities/Projectile';
import type { NoiseSystem } from './NoiseSystem';
import type { AABB } from '../core/Collision';
import { circleVsAABB } from '../core/Collision';
import { Vector2 } from '../core/Vector2';
import type { WeaponType, WeaponConfig } from '@hamsters/game-config';
import { WEAPON_CONFIGS } from '@hamsters/game-config';

export class CombatSystem {
  private weaponType: WeaponType | null = null;
  private ammo = 0;
  private fireCooldown = 0;
  private projectiles: Projectile[] = [];
  private parentContainer: PIXI.Container | null = null;

  constructor() {}

  setWeapon(type: WeaponType | null): void {
    this.weaponType = type;
  }

  setAmmo(amount: number): void {
    this.ammo = Math.max(0, amount);
  }

  getAmmo(): number {
    return this.ammo;
  }

  getWeaponType(): WeaponType | null {
    return this.weaponType;
  }

  getWeaponConfig(): WeaponConfig | null {
    if (!this.weaponType) return null;
    return WEAPON_CONFIGS[this.weaponType];
  }

  hasWeapon(): boolean {
    return this.weaponType !== null;
  }

  setParentContainer(container: PIXI.Container): void {
    this.parentContainer = container;
  }

  tryShoot(
    player: Player,
    tick: number,
    noiseSystem: NoiseSystem,
    _enemies: Enemy[],
    _walls: AABB[],
  ): boolean {
    if (!this.weaponType || this.ammo <= 0 || this.fireCooldown > 0) return false;

    const config = WEAPON_CONFIGS[this.weaponType];
    if (!config) return false;

    // Consume ammo
    this.ammo--;
    this.fireCooldown = config.fireRateMs;

    // Create projectile
    const dir = player.getDirection();
    const projectile = new Projectile(
      player.position.x,
      player.position.y,
      dir,
      config.projectileSpeed,
      config.damage,
    );

    this.projectiles.push(projectile);
    if (this.parentContainer) {
      this.parentContainer.addChild(projectile.sprite);
    }

    // Create noise event
    noiseSystem.createNoise('SHOT', player.position.x, player.position.y, tick, config.noiseRadius);

    return true;
  }

  update(dt: number, _tick: number, enemies: Enemy[], walls: AABB[]): void {
    // Update fire cooldown
    if (this.fireCooldown > 0) {
      this.fireCooldown -= dt * 1000;
    }

    // Update projectiles
    this.projectiles = this.projectiles.filter((proj) => {
      if (!proj.active) {
        if (proj.sprite.parent) {
          proj.sprite.parent.removeChild(proj.sprite);
        }
        proj.destroy();
        return false;
      }

      proj.update(dt);

      // Check wall collision
      for (const wall of walls) {
        if (circleVsAABB(
          { x: proj.position.x, y: proj.position.y, radius: proj.radius },
          wall,
        )) {
          proj.active = false;
          if (proj.sprite.parent) {
            proj.sprite.parent.removeChild(proj.sprite);
          }
          proj.destroy();
          return false;
        }
      }

      // Check enemy collision
      for (const enemy of enemies) {
        if (!enemy.isAlive()) continue;
        const dx = proj.position.x - enemy.position.x;
        const dy = proj.position.y - enemy.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < proj.radius + enemy.radius) {
          enemy.takeDamage(proj.getDamage());
          enemy.onPlayerSpotted(new Vector2(proj.position.x, proj.position.y));
          proj.active = false;
          if (proj.sprite.parent) {
            proj.sprite.parent.removeChild(proj.sprite);
          }
          proj.destroy();
          return false;
        }
      }

      // Out of map bounds
      if (proj.position.x < 0 || proj.position.x > 2048 ||
          proj.position.y < 0 || proj.position.y > 2048) {
        proj.active = false;
        if (proj.sprite.parent) {
          proj.sprite.parent.removeChild(proj.sprite);
        }
        proj.destroy();
        return false;
      }

      return true;
    });
  }

  clear(): void {
    for (const proj of this.projectiles) {
      if (proj.sprite.parent) {
        proj.sprite.parent.removeChild(proj.sprite);
      }
      proj.destroy();
    }
    this.projectiles = [];
  }
}
